import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'danger' | 'warning' | 'success';
  className?: string;
}

export const Modal = ({ 
  isOpen, onClose, title, children, footer, 
  size = 'md', className = '', variant = 'default' 
}: ModalProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl'
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-0">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />
      
      {/* Modal Content */}
      <div className={`bg-white rounded-md shadow-sm w-full ${sizeClasses[size]} ${className} relative z-10 flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200`}>
        <div className={`flex items-center justify-between px-6 py-4 border-b ${variant === 'danger' ? 'border-red-100 bg-red-50/30' : variant === 'warning' ? 'border-amber-100 bg-amber-50/30' : variant === 'success' ? 'border-emerald-100 bg-emerald-50/30' : 'border-slate-100'}`}>
          <h3 className={`sm:text-sm text-xs ${variant === 'danger' ? 'text-red-900' : variant === 'warning' ? 'text-amber-900' : variant === 'success' ? 'text-emerald-900' : 'text-slate-900'}`}>{title}</h3>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors rounded-md p-1.5 hover:bg-slate-100 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto sm:text-sm text-xs">
          {children}
        </div>
        
        {footer && (
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-md flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
